// 기존 유저 이관(docs/CHARACTER-PRD.md §1 「기존 유저」·§13.3): 계정 공유 진행 키 → 캐릭터별 키. 손실 0 · 복제 0 · 멱등.
//
// 규칙
// - 원래 키(dungeonhero.*)에 진행이 하나라도 있으면 카엘 진행 → 검사 캐릭터, 라이라 레벨 2 이상 → 궁수 캐릭터.
// - 레벨이 높은 캐릭터 H(같으면 검사)가 원래 진행을 통째로 받는다(골드·재료·장비·소비템·상점 장부·첫 클리어 대기 보상·레이드 정산 대기).
// - 다른 캐릭터 L은 진행 기록만 복사받는다: 지역 기록·스테이지(노말·하드)·일일·레이드 기록. 셀 수 있는 것은 0:
//     save.v1 골드 0(획득 기록은 그대로, 고르지 않은 카드 영수증은 뺀다 — H에서 고른다),
//     profile.v2 준비금 없음·상점 장부 비움·옛 장비 전환 끝 표시(다시 전환해 옛 장비가 생기지 않게),
//     stage.v1·stage.hard.v1의 받는 중(owed) 첫 클리어 보상은 수령(claimed)으로(H가 받는다 — L이 장부에 다시 넣지 않게),
//     raid.v1 정산 표시 = H 표시 ∪ save.v1의 모든 레이드 영수증(L이 레이드 보상을 다시 정산하지 않게).
// - 무기는 영웅별: 장비 장부에서 L 영웅 전용 무기(heroLock)는 L에게 옮기고 H에서 뺀다. 그 밖의 장비·강화석·보석·소비템·골드 순지출은 H.
// - 성장(growth)·스킬 슬롯은 그 영웅의 키만 그 캐릭터에게(카엘 v1·v2 → 검사, 라이라 → 궁수).
// - 원래 키는 지우지 않는다(이전 빌드로 되돌리면 이관 시점 진행이 그대로 보인다 — 롤백 호환).
//
// 멱등: 결과는 원래 키만으로 결정된다(캐릭터 id도 계정 id에서 결정적으로 만든다 — 두 기기가 같은 진행을 이관해도 같은 캐릭터가 된다).
// 쓰는 순서: 캐릭터 키 전부 → 되읽어 확인 → 목록(roster)에 이관 끝 표시. 중간에 멈추면 다음 부팅이 같은 값을 다시 쓴다(표시가 없으면 캐릭터를 켜지 않는다).
// 표시가 있으면 다시 하지 않는다(이관 뒤 진행을 덮지 않게).
import { COPY_SUFFIXES, SYNC_KEYS, type KV } from '../account/keys';
import { GrowthStore } from '../game/growth';
import { applyGearOp, cloneGear, freshGear, parseGear, serializeGear, type GearOp, type GearState } from '../shared/gearLedger';
import { parseRaidReceipt } from '../shared/content/raid';
import { raidFirstReward, raidReward } from '../shared/content/raidReward';
import { owedGearOps } from '../game/owedReward';
import { parseStageSave } from '../game/progress';
import { parseHardSave } from '../game/hardProgress';
import type { HeroId } from '../shared/content/types';
import { SaveStore, classifySaveRaw } from '../game/save';
import { equipDef } from '../shared/gear';
import { LEGACY_ITEM_IDS } from '../shared/content/legacyGear';
import { DEFAULT_WEAPON } from '../shared/rpg';
import { charKey } from './keys';
import type { ClassId } from './protocol';
import { PROGRESS_COPY, allocationHash, type MigrationAllocation, type MigrationHero } from './allocation';

const K = {
  save: 'dungeonhero.save.v1', profile: 'dungeonhero.profile.v2',
  growthV1: 'dungeonhero.growth.v1', growthV2: 'dungeonhero.growth.v2', growthLyra: 'dungeonhero.growth.lyra.v1',
  slots: 'dungeonhero.skillslots.v1', slotsLyra: 'dungeonhero.skillslots.lyra.v1',
  gear: 'dungeonhero.gear.v1', stage: 'dungeonhero.stage.v1', hard: 'dungeonhero.stage.hard.v1', hell: 'dungeonhero.stage.hell.v1',
  daily: 'dungeonhero.daily.v1', hero: 'dungeonhero.hero.v1', raid: 'dungeonhero.raid.v1',
} as const;
/** 영웅마다 자기 것만 가져가는 키 */
const OWN_KEYS: Readonly<Record<'kael' | 'lyra', readonly string[]>> = {
  kael: [K.growthV1, K.growthV2, K.slots],
  lyra: [K.growthLyra, K.slotsLyra],
};
const OTHER_HERO_KEYS = new Set([...OWN_KEYS.kael, ...OWN_KEYS.lyra]);
/** 라이라 캐릭터를 만드는 최소 레벨 */
export const LYRA_MIN_LEVEL = 2;

type Obj = Record<string, unknown>;
const obj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);
function parse(raw: string | null): Obj | null {
  if (raw === null) return null;
  try { const v: unknown = JSON.parse(raw); return obj(v) ? v : null; } catch { return null; }
}

/** 결정적 캐릭터 id: 'c_' + 24 hex(씨앗 = 계정 id 또는 기기 씨앗, 영웅). 보안 값이 아니다(서버가 계정 소유를 확인한다) */
export function migratedCharacterId(seed: string, hero: string): string {
  const text = 'dh-char-migrate:' + seed + ':' + hero;
  let out = '';
  for (const salt of [0x811c9dc5, 0x2b5d1a37, 0x9e3779b9]) {
    let h = salt >>> 0;
    for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193); h ^= h >>> 13; }
    h = Math.imul(h ^ (h >>> 16), 0x85ebca6b); h ^= h >>> 13;
    out += (h >>> 0).toString(16).padStart(8, '0');
  }
  return 'c_' + out;
}

/** 저장 모듈이 .good 사본으로 손상을 복구하는 키(save·gear·growth). 캐릭터에게는 복구된 값을 주 키와 .good에 함께 쓴다 */
const GOOD_RECOVERY = new Set<string>([K.save, K.gear, K.growthV1, K.growthV2, K.growthLyra]);

/** 이관이 보는 원래 진행 상태 하나: 각 저장 모듈의 검증된 복구 경로로 먼저 읽은 값. 배분 계획과 캐릭터 스냅샷은 이것만 본다(원래 키를 날것으로 읽지 않는다) */
export interface LegacyState {
  /** 영웅 레벨(GrowthStore가 연 값) */
  levels: { kael: number; lyra: number };
  /** 키 → 쓸 값(복구된 값, 없으면 null). 진행 키(SYNC_KEYS)의 주 키만 */
  values: Record<string, string | null>;
  /** 장비 장부(검증된 상태 — 주 키가 손상이면 .good 복구). 읽을 수 없거나 미래 버전이면 null(나누지 않는다) */
  gear: GearState | null;
  /** .good 사본으로 복구한 키(조사용) */
  recovered: string[];
  /** 이관 전에 장비 장부에 정산한 보상(레이드 영수증 id · owed:<스테이지>) */
  settled: string[];
}

/**
 * 원래 키 → LegacyState. 저장 모듈과 같은 규칙:
 * - save.v1: SaveStore — 주 키가 손상(파싱 불가·객체 아님)이면 .good(정상 v1일 때만). 미래 형식이면 주 키 그대로(사본 복구 없음)
 * - gear.v1: GearStore(parseGear) — 손상이면 .good. 미래 버전이면 주 키 그대로, 나누지 않음
 * - growth v1·v2·lyra: GrowthStore를 덧칠 저장소 위에서 열어 복구·v1 이관까지 한 값(실제 저장소에는 쓰지 않는다)
 * - 그 밖(stage·hard·daily·raid·profile·skillslots·hero): 저장 모듈이 사본에서 되살리지 않는다(.bak은 손상 원문 보관) → 주 키 그대로
 */
export function readLegacyState(kv: KV): LegacyState {
  const values: Record<string, string | null> = {};
  const recovered: string[] = [];
  for (const k of SYNC_KEYS) values[k] = kv.getItem(k);
  // save
  const save = values[K.save];
  if (save !== null && classifySaveRaw(save) === 'corrupt') {
    const good = kv.getItem(K.save + '.good');
    values[K.save] = good !== null && classifySaveRaw(good) === 'ok' ? good : null;
    if (values[K.save] !== null) recovered.push(K.save);
  }
  // gear
  let gear: GearState | null = null;
  const gr = values[K.gear];
  if (gr !== null) {
    const p = parseGear(gr);
    if (p.ok) gear = p.state;
    else if (p.reason === 'corrupt') {
      const good = kv.getItem(K.gear + '.good');
      const pg = good !== null ? parseGear(good) : null;
      if (pg && pg.ok) { gear = pg.state; values[K.gear] = good; recovered.push(K.gear); }
      else values[K.gear] = null;
    }
  }
  // 받는 중 보상 정산(이관 전): 정산 표시가 없는 레이드 영수증의 보상 연산과 첫 클리어 대기(owed) 연산을 장비 장부에 같은 id로 넣는다.
  // 그래야 보상 장비(예: 라이라 전설 활)가 배분 전에 장부에 있어 받을 영웅에게 간다(Codex P1 #214 5차). 같은 id 연산은 장부가 거르므로 몇 번 해도 한 번이다
  const settled = settlePending(values, gear);
  if (settled) { gear = settled.gear; values[K.gear] = serializeGear(gear); values[K.raid] = settled.raid; }
  // growth: GrowthStore가 복구·이관하며 쓴 값은 덧칠 저장소에만 남는다
  const levels = { kael: 1, lyra: 1 };
  for (const hero of ['kael', 'lyra'] as const) {
    const overlay = new Map<string, string | null>();
    const view = {
      getItem: (k: string): string | null => (overlay.has(k) ? overlay.get(k)! : kv.getItem(k)),
      setItem: (k: string, v: string): void => { overlay.set(k, v); },
      removeItem: (k: string): void => { overlay.set(k, null); },
    };
    try { levels[hero] = new GrowthStore(view, { hero, nonce: 'char-migrate' }).level; } catch { levels[hero] = 1; }
    for (const k of hero === 'kael' ? [K.growthV2] : [K.growthLyra]) {
      if (overlay.has(k)) { const v = overlay.get(k)!; if (v !== values[k]) recovered.push(k); values[k] = v; }
    }
  }
  return { levels, values, gear, recovered, settled: settled?.ids ?? [] };
}

/** 레이드 미정산 보상 + 첫 클리어 대기 보상을 장비 장부에 넣는다(바뀐 것이 없으면 null). 장부가 미래 버전 등으로 읽을 수 없으면(gear null인데 원문이 있음) 건드리지 않는다 */
function settlePending(values: Record<string, string | null>, gear: GearState | null): { gear: GearState; raid: string | null; ids: string[] } | null {
  if (gear === null && values[K.gear] !== null) return null;
  // 모든 원래 값은 저장 모듈의 파서를 거친 것만 쓴다(Codex P2 #214 — 잘못된 owed·영수증이 이관 전체를 멈추지 않게)
  // 레이드 영수증: SaveStore가 검증한 영수증(reward 형식)만 · 정산 표시: RaidStore와 같은 형식(v 1, 문자열 배열)
  const store = new SaveStore({ getItem: (k) => (k === K.save ? values[K.save] : null), setItem: () => {} }, { locks: undefined });
  const claimed = new Set<string>(parseRaidClaimed(values[K.raid]));
  const ops: GearOp[] = [];
  const ids: string[] = [];
  store.forEachRaidReceipt((id) => {
    if (claimed.has(id)) return;
    const at = parseRaidReceipt(id), r = store.getResult(id);
    if (!at || !r) return;
    const hero = (r.raidHero === 'lyra' ? 'lyra' : 'kael') as HeroId;
    const rw = at.first ? raidFirstReward(at.raid, hero) : raidReward(at.raid, at.week, at.n, hero, (r.raidProtect ?? 0) > 0, 1, at.diff);
    if (!rw) return;
    ops.push(...rw.ops);
    claimed.add(id); ids.push(id);
  });
  // 첫 클리어 대기: parseStageSave·parseHardSave(owed는 parseOwedMap)가 통과시킨 것만
  for (const k of [K.stage, K.hard, K.hell]) {
    const st = k === K.stage ? parseStageSave(parseAny(values[k])) : parseHardSave(parseAny(values[k]), k === K.hell ? 'hell' : 'hard');
    if (!st) continue;
    for (const [key, owed] of Object.entries(st.owed)) {
      const list = owedGearOps(owed);
      if (list.length) { ops.push(...list); ids.push('owed:' + key); }
    }
  }
  if (!ops.length) return null;
  const g = gear ? cloneGear(gear) : freshGear();
  let changed = false;
  for (const op of ops) if (applyGearOp(g, op).code === 'ok') changed = true;
  if (changed) { g.rev += 1; g.by = 'char-migrate'; }
  return { gear: g, raid: claimed.size ? JSON.stringify({ v: 1, claimed: [...claimed].sort() }) : values[K.raid], ids };
}

/** 원래 키에서 영웅 레벨(readLegacyState의 레벨 — GrowthStore 복구 경로) */
export function legacyLevels(kv: KV): { kael: number; lyra: number } { return readLegacyState(kv).levels; }

/** 원래 키에 진행이 하나라도 있는가(없으면 새 유저 — 이관할 것이 없다) */
export function hasLegacyProgress(kv: KV): boolean {
  for (const k of SYNC_KEYS) if (kv.getItem(k) !== null) return true;
  return false;
}

export interface PlannedCharacter { id: string; cls: ClassId; origin: 'legacy-kael' | 'legacy-lyra'; level: number; keys: Record<string, string> }
/** allocation = 이 계획의 배분 문서(서버에 올리거나 받은 것), hash = allocationHash(allocation) */
export interface MigrationPlan { characters: PlannedCharacter[]; higher: string; allocation: MigrationAllocation; hash: string }

function heroSelect(hero: 'kael' | 'lyra'): string { return JSON.stringify({ version: 1, hero }); }

/** L의 save.v1: 골드 0, 고르지 않은 카드 영수증 빼기(나머지 기록·영수증은 그대로) */
function lowerSave(raw: string | null): string | null {
  const s = parse(raw);
  if (!s) return null;
  const out: Obj = { ...s, gold: 0 };
  const rr = s.rewardResults;
  if (obj(rr) && obj(rr.entries)) {
    const entries: Obj = {};
    for (const [id, r] of Object.entries(rr.entries)) if (!(obj(r) && r.pickedCard === -1)) entries[id] = r;
    out.rewardResults = { ...rr, entries };
  }
  return JSON.stringify(out);
}
/** L의 stage.v1·stage.hard.v1: 받는 중(owed) → 수령(claimed). 저장 모듈 파서를 통과하지 못하면 L은 이 진행 없이 시작(H는 원문 그대로) */
function lowerStage(raw: string | null, diff: false | 'hard' | 'hell' = false): string | null {
  const s = parse(raw);
  if (!s || !(diff ? parseHardSave(s, diff) : parseStageSave(s))) return null;
  const claimed: Obj = obj(s.claimed) ? { ...s.claimed } : {};
  if (obj(s.owed)) for (const k of Object.keys(s.owed)) claimed[k] = 1;
  return JSON.stringify({ ...s, claimed, owed: {} });
}
function parseAny(raw: string | null): unknown { if (raw === null) return null; try { return JSON.parse(raw) as unknown; } catch { return null; } }
/** 레이드 정산 표시(RaidStore와 같은 형식: v 1, 문자열 배열 — 틀리면 빈 표시) */
function parseRaidClaimed(raw: string | null): string[] {
  const r = parse(raw);
  return r && r.v === 1 && Array.isArray(r.claimed) ? r.claimed.filter((x): x is string => typeof x === 'string' && x.length < 80) : [];
}
/** L의 raid.v1: 정산 표시 ∪ save.v1의 레이드 영수증 */
function lowerRaid(raidRaw: string | null, saveRaw: string | null): string | null {
  const ids = new Set<string>(parseRaidClaimed(raidRaw));
  const s = parse(saveRaw);
  const rr = s?.rewardResults;
  if (obj(rr) && obj(rr.entries)) for (const id of Object.keys(rr.entries)) if (id.startsWith('raid:')) ids.add(id);
  return ids.size || raidRaw !== null ? JSON.stringify({ v: 1, claimed: [...ids].sort() }) : null;
}
/** L의 profile.v2: 준비금 없음(새 캐릭터 준비금 300을 만들지 않게 명시), 장부 비움, 옛 장비 전환 끝 */
function lowerProfile(): string {
  return JSON.stringify({ version: 2, starter: null, txs: [], equip: { weapon: DEFAULT_WEAPON, armor: null, focus: 0, rev: 0, by: '' }, gearUnified: [...LEGACY_ITEM_IDS].sort() });
}
/** 장비 개체의 받는 영웅: 계획에 있으면 그 값, 없으면 규칙(낮은 캐릭터 영웅 전용 무기 → 낮은 캐릭터, 나머지 → 높은 캐릭터) */
function gearOwner(uid: string, def: string, heroes: readonly MigrationHero[], high: MigrationHero, plan: Readonly<Record<string, MigrationHero>> | null): MigrationHero {
  const p = plan?.[uid];
  if (p && heroes.includes(p)) return p;
  const lock = equipDef(def)?.heroLock;
  return lock && lock !== high && heroes.includes(lock as MigrationHero) ? lock as MigrationHero : high;
}
/** 장비 장부 나누기: 높은 캐릭터 장부(골드 순지출·재료·소비템 포함)와 낮은 캐릭터 장부(옮긴 개체만). 장부를 읽을 수 없거나 옮길 개체가 없으면 null(높은 캐릭터가 원문 그대로) */
function splitGear(state: GearState | null, low: MigrationHero, heroes: readonly MigrationHero[], high: MigrationHero, plan: Readonly<Record<string, MigrationHero>> | null): { high: string; low: string } | null {
  if (state === null) return null;
  const hi: GearState = cloneGear(state);
  const lo = freshGear();
  const moved = new Set<string>();
  for (const it of hi.items) if (gearOwner(it.uid, it.def, heroes, high, plan) === low) moved.add(it.uid);
  if (!moved.size) return null;
  lo.items = hi.items.filter((it) => moved.has(it.uid));
  hi.items = hi.items.filter((it) => !moved.has(it.uid));
  for (const slot of Object.keys(hi.equipped) as (keyof GearState['equipped'])[]) {
    const uid = hi.equipped[slot];
    if (uid && moved.has(uid)) { lo.equipped[slot] = uid; hi.equipped[slot] = null; }
  }
  // 영웅별 무기 칸(v5, lyra-weapon-slot)도 개체를 따라간다
  for (const h of Object.keys(hi.hw) as (keyof GearState['hw'])[]) {
    const uid = hi.hw[h];
    if (uid && moved.has(uid)) { lo.hw[h] = uid; delete hi.hw[h]; }
  }
  hi.rev += 1; hi.by = 'char-migrate';
  lo.rev = 1; lo.by = 'char-migrate';
  return { high: serializeGear(hi), low: serializeGear(lo) };
}

/**
 * 이관 계획(쓰지 않는다): 원래 키만 읽는다. 진행이 없으면 null.
 * seed: 계정 id(있으면) 또는 기기 씨앗 — 캐릭터 id를 정한다.
 */
export function planMigration(kv: KV, seed: string): MigrationPlan | null {
  if (!hasLegacyProgress(kv)) return null;
  const st = readLegacyState(kv);
  const lv = st.levels;
  const heroes: MigrationHero[] = lv.lyra >= LYRA_MIN_LEVEL ? ['kael', 'lyra'] : ['kael'];
  const high: MigrationHero = heroes.length === 2 && lv.lyra > lv.kael ? 'lyra' : 'kael';
  const low = heroes.length === 2 ? (high === 'kael' ? 'lyra' : 'kael') : null;
  const idOf = (h: MigrationHero): string => migratedCharacterId(seed, h);
  const get = (k: string): string | null => st.values[k] ?? null;
  const gear = low ? splitGear(st.gear, low, heroes, high, null) : null;
  const chars: PlannedCharacter[] = [];
  for (const hero of heroes) {
    const keys: Record<string, string> = {};
    /** 주 키 + (.good 복구 키면) 같은 값의 .good — 옛 사본·손상 원문은 캐릭터에게 주지 않는다(복구가 다른 캐릭터 몫을 되살리지 않게) */
    const put = (k: string, v: string | null): void => {
      if (v === null) return;
      keys[k] = v;
      if (GOOD_RECOVERY.has(k)) keys[k + '.good'] = v;
    };
    const own = new Set(OWN_KEYS[hero]);
    if (hero === high) {
      // H: 복구된 원래 진행 전부 — 다른 영웅 성장·슬롯·영웅 선택은 빼고, 장비는 나눈 값
      for (const k of SYNC_KEYS) {
        if ((OTHER_HERO_KEYS.has(k) && !own.has(k)) || k === K.hero) continue;
        put(k, k === K.gear && gear ? gear.high : get(k));
      }
    } else {
      // L: 진행 기록만(셀 수 있는 것 0). 자기 영웅 성장·슬롯은 복구된 값
      for (const k of OWN_KEYS[hero]) put(k, get(k));
      put(K.save, lowerSave(get(K.save)));
      put(K.stage, lowerStage(get(K.stage)));
      put(K.hard, lowerStage(get(K.hard), 'hard'));
      put(K.hell, lowerStage(get(K.hell), 'hell'));
      put(K.daily, get(K.daily));
      put(K.raid, lowerRaid(get(K.raid), get(K.save)));
      keys[K.profile] = lowerProfile();
      if (gear) put(K.gear, gear.low);
    }
    keys[K.hero] = heroSelect(hero);
    chars.push({ id: idOf(hero), cls: hero, origin: hero === 'kael' ? 'legacy-kael' : 'legacy-lyra', level: lv[hero], keys });
  }
  // 배분 문서(서버 기록·planId = 해시): 만들 캐릭터·id·높은 캐릭터·정산 뒤 장부의 개체 배분
  const gearMap: Record<string, MigrationHero> = {};
  if (st.gear) for (const it of st.gear.items) gearMap[it.uid] = low ? gearOwner(it.uid, it.def, heroes, high, null) : high;
  const ids: Partial<Record<MigrationHero, string>> = {};
  for (const h of heroes) ids[h] = idOf(h);
  const allocation: MigrationAllocation = { v: 1, heroes, high, ids, gear: gearMap, copy: [...PROGRESS_COPY], levels: { kael: lv.kael, lyra: lv.lyra } };
  return { characters: chars, higher: idOf(high), allocation, hash: allocationHash(allocation) };
}

export type MigrationResult =
  | { kind: 'migrated'; plan: MigrationPlan }
  | { kind: 'failed'; reason: 'write' | 'verify' };

/**
 * 계획대로 캐릭터 키를 쓴다(원래 키는 그대로). 쓰기·되읽기가 다 맞아야 migrated.
 * 실패하면 쓴 캐릭터 키를 그대로 둔다 — 이관 표시가 없으니 켜지지 않고, 다음 부팅이 같은 값을 다시 쓴다.
 */
export function writeMigration(kv: KV, plan: MigrationPlan): MigrationResult {
  try {
    for (const c of plan.characters) {
      // 이 캐릭터의 진행 키 자리를 먼저 비운다(재실행 때 계획에 없는 사본이 남지 않게 — 결과가 원래 키만으로 정해진다)
      for (const k of SYNC_KEYS) for (const s of ['', ...COPY_SUFFIXES]) if (!Object.hasOwn(c.keys, k + s)) kv.removeItem(charKey(c.id, k + s));
      for (const [k, v] of Object.entries(c.keys)) kv.setItem(charKey(c.id, k), v);
    }
  } catch { return { kind: 'failed', reason: 'write' }; }
  try {
    for (const c of plan.characters) for (const [k, v] of Object.entries(c.keys)) if (kv.getItem(charKey(c.id, k)) !== v) return { kind: 'failed', reason: 'verify' };
  } catch { return { kind: 'failed', reason: 'verify' }; }
  return { kind: 'migrated', plan };
}
